import type { UserId } from 'balus-common';
import { ViewModelIndividualPermissionLocales, type ViewModelIndividualPermissions } from './types';
import { ViewModelOperation, type ViewModelEntity } from '@view-model/domain/view-model';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { Button } from '@framework/ui/atoms/Button';
import { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { generateDefaultUserIcon } from '@framework/utils';
import { toast } from 'react-hot-toast';

type Props = {
  isOpen: boolean;
  onClose(): void;
  viewModel: ViewModelEntity;
  individualMembers: ViewModelIndividualPermissions;
  guestUserIds: Set<UserId>;
};

export const MemberRemoveModal: React.FC<Props> = ({
  isOpen,
  onClose,
  viewModel,
  individualMembers,
  guestUserIds,
}: Props) => {
  const [keyword, setKeyword] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<UserId[]>([]);
  const [processing, setProcessing] = useState(false);
  const changed = selectedUserIds.length > 0;

  const handleClose = useCallback(() => {
    setSelectedUserIds([]);
    setKeyword('');
    setProcessing(false);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    setProcessing(true);

    try {
      await ViewModelOperation.removeIndividualPermissions(viewModel.id, selectedUserIds);
      toast.success('ビューモデルからメンバーを削除しました');
      handleClose();
    } catch (e) {
      toast.error('ビューモデルからメンバー削除に失敗しました');
      throw e;
    } finally {
      setProcessing(false);
    }
  }, [handleClose, selectedUserIds, viewModel.id]);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      title={`${viewModel.name.value}のメンバーを削除する`}
      submitButton={
        <Button color="danger" onClick={handleSubmit} disabled={!changed || processing} loading={processing}>
          メンバーを削除
        </Button>
      }
      cancelButton={
        <Button color="secondary" onClick={handleClose} disabled={processing}>
          キャンセル
        </Button>
      }
      containerClassName="overflow-scroll"
      containerStyle={{ maxHeight: '50vh' }}
    >
      <div className="mb-4 mt-2 overflow-y-auto p-2" style={{ width: '600px', minHeight: '250px', maxHeight: '50vh' }}>
        <div className="relative p-2">
          <div className="flex min-w-0 flex-1">
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="min-w-0 flex-1 rounded-md border-2 border-gray-400 py-1 pr-2 placeholder:text-sm focus:outline-none"
              placeholder="検索"
              style={{ paddingInlineStart: '32px' }}
            />
          </div>
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute flex-none text-gray-600"
            style={{ top: '20px', left: '20px' }}
          />
        </div>

        {individualMembers.length === 0 ? (
          <div className="flex justify-center px-2 py-4">削除対象のメンバーが見つかりません。</div>
        ) : (
          <ul className="px-2">
            {individualMembers
              .filter(([profile]) => profile.match(keyword))
              .map(([profile, role]) => (
                <li key={profile.id} className="flex flex-row items-center px-3 py-2 hover:bg-gray-200">
                  <label className="flex grow cursor-pointer flex-row truncate">
                    <input
                      type="checkbox"
                      className="form-checkbox m-1 size-6 border-brand"
                      checked={selectedUserIds.includes(profile.id)}
                      onChange={(e) =>
                        e.target.checked
                          ? setSelectedUserIds((prev) => [...prev, profile.id])
                          : setSelectedUserIds((prev) => prev.filter((id) => id !== profile.id))
                      }
                    />
                    <span className="my-1 ml-1 flex size-6 overflow-hidden rounded-full">
                      {profile.imageUrl ? (
                        <img className="size-full object-cover" src={profile.imageUrl} alt={profile.name} />
                      ) : (
                        <div className="size-full object-cover">
                          {generateDefaultUserIcon({
                            id: profile.id,
                            name: profile.name,
                            size: 24,
                          })}
                        </div>
                      )}
                    </span>
                    <span className="my-1 flex h-6 grow truncate px-2 leading-6">{profile.name}</span>
                    {guestUserIds.has(profile.id) && (
                      <span className="m-1 rounded-lg bg-gray-400 px-2 text-xs leading-6">ゲスト</span>
                    )}

                    <span className="my-1 w-16 justify-end leading-6">
                      {ViewModelIndividualPermissionLocales[role]}
                    </span>
                  </label>
                </li>
              ))}
          </ul>
        )}
      </div>
    </GeneralModal>
  );
};
